// ---------------------------------------------------------------------------
// urgency — pure classification of an alert item into a badge level
// ---------------------------------------------------------------------------
//
// The single owner of the legacy badge rules. Distinct from INCLUSION (which is
// the backend's request-time date-window filter). `asOf` is the SAME server date
// the backend filtered against (from the /alerts response), NOT the browser
// clock — so badge color can't diverge from inclusion across midnight/timezones.
// All comparisons are calendar-day on `YYYY-MM-DD` (no millisecond math).

/**
 * @typedef {import('../../contracts/dashboard.js').AlertItem} AlertItem
 * @typedef {import('../../contracts/dashboard.js').UrgencyLevel} UrgencyLevel
 */

/**
 * Parse a `YYYY-MM-DD` string to a UTC day-count integer (days since epoch).
 * @param {unknown} ymd
 * @returns {number}
 */
function dayNumber(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd ?? ''));
  if (!m) return NaN;
  const utc = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Math.floor(utc / 86400000);
}

/**
 * Classify one alert item for badge rendering.
 *
 * Rules (inclusive `≤`, matching legacy PHP `<=`):
 *  - banners / advertorials: expiresAt ≤ asOf+7d → urgent (danger); else prolong (success).
 *  - catalog: expiresAt ≤ asOf → expired (warning); ≤ asOf+7d → urgent (danger); else prolong (success).
 *  - events: status 'create' → translate (danger); 'edit' → update-translation (success).
 *
 * @param {{ expiresAt?: string, status?: string }} item - the fields urgency reads.
 * @param {string} asOf - server date `YYYY-MM-DD`.
 * @param {'banners'|'catalog'|'advertorials'|'events'} kind
 * @returns {UrgencyLevel}
 */
export function classifyUrgency(item, asOf, kind) {
  if (kind === 'events') {
    if (item?.status === 'create') {
      return { level: 'translate', label: 'translate', variant: 'danger' };
    }
    return { level: 'update-translation', label: 'update translation', variant: 'success' };
  }

  const today = dayNumber(asOf);
  const exp = dayNumber(item?.expiresAt);
  const weekOut = today + 7;

  if (kind === 'catalog' && Number.isFinite(exp) && exp <= today) {
    return { level: 'expired', label: 'EXPIRED', variant: 'warning' };
  }
  if (Number.isFinite(exp) && exp <= weekOut) {
    return { level: 'urgent', label: 'urgent', variant: 'danger' };
  }
  return { level: 'prolong', label: 'prolong or it expires', variant: 'success' };
}
